import React, { useEffect, useState, useRef } from "react";
import clsx from "clsx";
import { Dialog, DialogContent, DialogActions } from "@mui/material";
import { Stack, SelectField, MenuItem, Button, TextField, Alert } from "@curves/ui-base";
import GradientColorHeader from "@icoach/components/dialog/GradientColorHeader";
import { EmployeeSelect, ReadTextField } from "@icoach/components";
import useDeviceOrderApi from "@apis/useDeviceOrderApi";
import { refIsRequiredError, isEqual } from "@util/utils";
import { QUIT_REASON_TYPE } from "@icoach/constants/dataEnums";

const SmartFitnessBandDeleteContractDialog = React.forwardRef((props, ref) => {
	const { sourceData: sourceDataProps, open: openProps = false, onClose: onCloseProps, refresh: doRefreshProps } = props;
	if (!openProps) return null;
	const [options, setOptions] = useState({ employeeOptions: [], quitReasonTypeOptions: [] });
	const [selectedReasonType, setSelectedReasonType] = useState(""); // 選擇刪除原因
	const { getDeviceOrderApplyOptionsApi, deleteDeviceOrderApplyApi } = useDeviceOrderApi();
	const employeeRef = useRef(null);
	const reasonTypeRef = useRef(null);
	const leaveReasonRef = useRef(null);
	const { employeeOptions, quitReasonTypeOptions } = options;
	const { deviceOrderID, nickName, displayName } = sourceDataProps || {};

	const getParams = () => {
		const employeeID = employeeRef.current.getResult();
		const reasonType = reasonTypeRef.current.getResult();
		const leaveReason = leaveReasonRef.current.getResult();
		return {
			employeeID,
			reasonType,
			leaveReason,
		};
	};

	const handleDeleteContractClick = () => {
		// 檢查基本必填欄位
		const basicFieldsError = refIsRequiredError(employeeRef, reasonTypeRef);

		// 如果選擇了「其他」，則刪除說明是必填
		let leaveReasonError = false;
		if (isEqual(selectedReasonType, QUIT_REASON_TYPE.OTHERS)) {
			leaveReasonError = refIsRequiredError(leaveReasonRef);
		}

		if (!basicFieldsError && !leaveReasonError) {
			const params = getParams();
			doDeleteContractApi(params, deviceOrderID);
		}
	};

	const intiGetOptions = async () => {
		const resp = await getDeviceOrderApplyOptionsApi();
		if (resp) {
			setOptions(resp);
		}
	};

	const doDeleteContractApi = async (params, id) => {
		const resp = await deleteDeviceOrderApplyApi(params, id);
		if (resp) {
			// 要先刷新在關閉視窗
			doRefreshProps();
			onCloseProps();
		}
	};

	// 選擇刪除原因
	const handleReasonTypeChange = event => {
		const value = event.target.value;
		setSelectedReasonType(value);
	};

	useEffect(
		() => {
			if (openProps) intiGetOptions();
			else setOptions({ employeeOptions: [], quitReasonTypeOptions: [] });
		},
		// eslint-disable-next-line
		[openProps]
	);

	return (
		<Dialog className={clsx("simple-content", "dialog")} PaperProps={{ className: "wx-40rem" }} open={openProps} fullWidth>
			<GradientColorHeader onClose={onCloseProps}>刪除智能系統合約書</GradientColorHeader>
			<DialogContent>
				<Stack spacing={2} direction="column">
					<ReadTextField label="暱稱">{nickName || displayName}</ReadTextField>
					<EmployeeSelect ref={employeeRef} options={employeeOptions} label="經辦教練" fullWidth required />
					<SelectField ref={reasonTypeRef} label="刪除原因" displayEmpty fullWidth required onChange={handleReasonTypeChange}>
						<MenuItem value={""} disabled>
							<em>請選擇</em>
						</MenuItem>
						{quitReasonTypeOptions.map(({ text, value }) => (
							<MenuItem key={value} value={value}>
								{text}
							</MenuItem>
						))}
					</SelectField>
					<TextField
						ref={leaveReasonRef}
						label="刪除說明"
						rows={3}
						multiline
						fullWidth
						required={isEqual(selectedReasonType, QUIT_REASON_TYPE.OTHERS)} // 如果選擇了「其他」，則刪除說明是必填
					/>
					<Alert severity="info" variant="filled" color="info" className="mb-3">
						提醒您！要記得去作廢或折讓此筆訂單；若初次付款為自動扣款訂單，請通知總部人員再煩請確認！
					</Alert>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button variant="contained" onClick={handleDeleteContractClick}>
					確認送出
				</Button>
			</DialogActions>
		</Dialog>
	);
});

export default SmartFitnessBandDeleteContractDialog;
